import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { courseApi } from '../api/courseApi';
import { weatherApi } from '../api/weatherApi';
import { CourseSearchItem, GpsLocation, SavedCourseDetail, WindConditions } from '../types';

const EARTH_RADIUS_METERS = 6_371_000;
const METERS_TO_YARDS = 1.09361;
const WIND_REFRESH_MILLISECONDS = 5 * 60 * 1000;

export type LocationStatus = 'unsupported' | 'requesting' | 'active' | 'denied' | 'unavailable';
export type CourseDetailStatus = 'idle' | 'loading' | 'ready' | 'unavailable';

const distanceYards = (from: GpsLocation, targetLat: number, targetLng: number): number => {
  const toRadians = (degrees: number) => degrees * Math.PI / 180;
  const fromLat = toRadians(from.latitude);
  const toLat = toRadians(targetLat);
  const latitudeDelta = toRadians(targetLat - from.latitude);
  const longitudeDelta = toRadians(targetLng - from.longitude);
  const a = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(fromLat) * Math.cos(toLat) * Math.sin(longitudeDelta / 2) ** 2;
  return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * METERS_TO_YARDS;
};

export const useRoundConditions = (course: CourseSearchItem | null, currentHole: number) => {
  const [courseDetail, setCourseDetail] = useState<SavedCourseDetail | null>(null);
  const [courseDetailStatus, setCourseDetailStatus] = useState<CourseDetailStatus>('idle');
  const [location, setLocation] = useState<GpsLocation | null>(null);
  const [locationStatus, setLocationStatus] = useState<LocationStatus>(
    'geolocation' in navigator ? 'requesting' : 'unsupported',
  );
  const [wind, setWind] = useState<WindConditions | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const lastWindRequest = useRef<{ at: number; target: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCourseDetail(null);
    if (!course) {
      setCourseDetailStatus('idle');
      return;
    }
    setCourseDetailStatus('loading');

    void courseApi.getByExternalId(course.id)
      .then((detail) => {
        if (!cancelled) {
          setCourseDetail(detail);
          setCourseDetailStatus('ready');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCourseDetail(null);
          setCourseDetailStatus('unavailable');
        }
      });

    return () => { cancelled = true; };
  }, [course]);

  const acceptPosition = useCallback((position: GeolocationPosition) => {
    setLocation({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    });
    setLocationStatus('active');
  }, []);

  const rejectPosition = useCallback((error: GeolocationPositionError) => {
    setLocationStatus(error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable');
  }, []);

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setLocationStatus('unsupported');
      return;
    }

    setLocationStatus('requesting');
    const watchId = navigator.geolocation.watchPosition(acceptPosition, rejectPosition, {
      enableHighAccuracy: true,
      maximumAge: 10_000,
      timeout: 15_000,
    });
    return () => navigator.geolocation.clearWatch(watchId);
  }, [acceptPosition, rejectPosition]);

  const currentHoleGeometry = useMemo(
    () => courseDetail?.holes.find((hole) => hole.number === currentHole),
    [courseDetail, currentHole],
  );

  const greenDistanceYards = useMemo(() => {
    if (!location || currentHoleGeometry?.greenLat == null || currentHoleGeometry.greenLng == null) return null;
    return distanceYards(location, currentHoleGeometry.greenLat, currentHoleGeometry.greenLng);
  }, [currentHoleGeometry, location]);

  const refresh = useCallback(() => {
    if (!('geolocation' in navigator)) return;
    setIsRefreshing(true);
    setLocationStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        acceptPosition(position);
        setIsRefreshing(false);
      },
      (error) => {
        rejectPosition(error);
        setIsRefreshing(false);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15_000 },
    );
  }, [acceptPosition, rejectPosition]);

  useEffect(() => {
    let cancelled = false;
    if (!location) {
      setWind(null);
      return;
    }

    const target = `${currentHoleGeometry?.greenLat ?? 'none'}:${currentHoleGeometry?.greenLng ?? 'none'}`;
    const previous = lastWindRequest.current;
    if (previous?.target === target && Date.now() - previous.at < WIND_REFRESH_MILLISECONDS) return;
    lastWindRequest.current = { at: Date.now(), target };

    void weatherApi.getWind(
      location.latitude,
      location.longitude,
      currentHoleGeometry?.greenLat,
      currentHoleGeometry?.greenLng,
    ).then((conditions) => {
      if (!cancelled) setWind(conditions);
    });

    return () => { cancelled = true; };
  }, [currentHoleGeometry, location]);

  return {
    courseDetail,
    courseDetailStatus,
    currentHoleGeometry,
    greenDistanceYards,
    isRefreshing,
    location,
    locationStatus,
    refresh,
    wind,
  };
};
